import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CCol, CFormInput, CFormLabel, CFormSelect,
  CRow,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { CUSTOM_DEVICE_TYPE, DeviceTypeSelect, deviceChosen } from '../components/DeviceTypeSelect';
import { RichTextEditor } from '../components/RichText';
import { useLookups } from '../hooks/useLookups';
import { priorityLabels } from '../format';
import { PracticeBanner, startPracticeForm } from '../tour/PracticeCoach';

type Company = Schemas['CompanyLookupResponse'];
type ItemRow = { portalDeviceTypeId: number | ''; customName: string; quantity: number; label: string };

/**
 * Založení zakázky po telefonu. Záměrně bez konfigurace — obchodník neví, jaká má být
 * průchodka; doplní ji vedení v detailu. Termín je nepovinný, server doplní týden.
 */
export function NewContractPage() {
  const navigate = useNavigate();
  const lookups = useLookups();
  // Krok 1 cviceni (ADR-019): stejny formular, jen predvyplneny a s pruvodcem.
  const practice = useSearchParams()[0].get('cvicne') === '1';

  // Zakaznik = firma z portalu. Zaklada se tam, tady se jen vybira (zpetna vazba 2. 10. 2026).
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companyId, setCompanyId] = useState('');
  // Cviceni: priklad, aby slo jen proklikat. Nazev hned, zarizeni az z nacteneho ciselniku.
  const [title, setTitle] = useState(practice ? 'Monitoring mostu přes Vltavu' : '');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [items, setItems] = useState<ItemRow[]>([{ portalDeviceTypeId: '', customName: '', quantity: 1, label: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Jakmile dorazi ciselnik, cviceni predvyplni zarizeni - jednou, behem vykresleni
  // (doporuceny vzor Reactu misto setState v efektu).
  const [prefilled, setPrefilled] = useState(false);
  if (practice && lookups && !prefilled) {
    setPrefilled(true);
    const chester = lookups.deviceTypes.filter((d) => d.name.includes('Chester')).at(-1) ?? lookups.deviceTypes[0];
    if (chester && items[0]?.portalDeviceTypeId === '') {
      setItems([{ ...items[0], portalDeviceTypeId: chester.portalDeviceTypeId, quantity: 2 }]);
    }
  }

  // Pruvodce az nad vyplnenym formularem - ukazuje na skutecna pole.
  const formReady = practice && lookups !== null;
  useEffect(() => {
    if (!formReady) return;
    let stop = () => {};
    const timer = window.setTimeout(() => { stop = startPracticeForm(); }, 400);
    return () => { window.clearTimeout(timer); stop(); };
  }, [formReady]);

  useEffect(() => {
    if (practice) return;
    api.GET('/api/v1/lookups/companies').then(({ data, error: problem }) => {
      if (data) setCompanies(data); else setError(problemMessage(problem));
    });
  }, [practice]);

  // Cvicna zakazka ma vlastniho zakaznika mimo portal (ADR-019) - firmu se nevybira.
  const customerReady = practice || companyId !== '';
  const itemsReady = items.every((i) => deviceChosen(i.portalDeviceTypeId, i.customName) && i.quantity >= 1);

  async function submit() {
    setBusy(true);
    setError(null);

    const { data, error: problem } = await api.POST('/api/v1/contracts', {
      body: {
        portalCompanyId: practice ? null : companyId,
        title: title.trim(),
        description: description || null,
        dueDate: dueDate || null,
        priority,
        items: items.map((i) => ({
          portalDeviceTypeId: Number(i.portalDeviceTypeId), quantity: i.quantity, label: i.label || null, note: null,
          customName: i.portalDeviceTypeId === CUSTOM_DEVICE_TYPE ? i.customName.trim() : null,
        })),
        isPractice: practice,
      },
    });

    setBusy(false);

    if (data) {
      navigate(`/zakazky/${data.id}`);
    } else {
      setError(problemMessage(problem));
    }
  }

  const setItem = (index: number, patch: Partial<ItemRow>) =>
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <>
      <h2 className="mb-4">Nová zakázka</h2>
      {practice && <PracticeBanner step={1} onExit={() => navigate('/')} />}

      <CRow className="g-4">
        <CCol lg={7}>
          <CCard className="mb-4" data-tour="nc-customer">
            <CCardHeader><strong>Zákazník</strong></CCardHeader>
            <CCardBody>
              {practice ? (
                <div className="fw-semibold">Cvičný zákazník s.r.o.</div>
              ) : (
                <>
                  <CFormSelect value={companyId} onChange={(e) => setCompanyId(e.target.value)} disabled={!companies} autoFocus>
                    <option value="">{companies ? 'Vyberte firmu…' : 'Načítám firmy z portálu…'}</option>
                    {companies?.map((c) => (
                      <option key={c.compId} value={c.compId}>{c.name}</option>
                    ))}
                  </CFormSelect>
                  <div className="small text-body-secondary mt-1">Firma chybí? Založte ji v portálu, tady se jen vybírá.</div>
                </>
              )}
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader><strong>Zakázka</strong></CCardHeader>
            <CCardBody>
              <CFormLabel>Název</CFormLabel>
              <CFormInput className="mb-3" data-tour="nc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="např. Monitoring jezu Lhotka" />
              <CFormLabel>Popis</CFormLabel>
              <div className="mb-3">
                <RichTextEditor value={description} onChange={setDescription} placeholder="Co zákazník chce, na co nezapomenout…" minHeight="5rem" />
              </div>
              <CRow className="g-3">
                <CCol>
                  <CFormLabel>Termín</CFormLabel>
                  <CFormInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                  <div className="small text-body-secondary mt-1">Když nevyplníte, bude za týden.</div>
                </CCol>
                <CCol>
                  <CFormLabel>Priorita</CFormLabel>
                  <CFormSelect value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={5}>
          <CCard className="mb-4" data-tour="nc-items">
            <CCardHeader><strong>Co se bude stavět</strong></CCardHeader>
            <CCardBody>
              {items.map((item, index) => (
                <CRow key={index} className="g-2 mb-2 align-items-start">
                  <CCol xs={6}>
                    <DeviceTypeSelect size="sm" deviceTypes={lookups?.deviceTypes} value={item.portalDeviceTypeId} customName={item.customName}
                      placeholder="Zařízení…" onChange={(value, name) => setItem(index, { portalDeviceTypeId: value, customName: name })} />
                  </CCol>
                  <CCol xs={2}>
                    <CFormInput size="sm" type="number" min={1} value={item.quantity} aria-label="Kusů"
                      onChange={(e) => setItem(index, { quantity: Number(e.target.value) })} />
                  </CCol>
                  <CCol xs={3}>
                    <CFormInput size="sm" placeholder="Označení" value={item.label} onChange={(e) => setItem(index, { label: e.target.value })} />
                  </CCol>
                  <CCol xs={1}>
                    {items.length > 1 && (
                      <CButton size="sm" color="secondary" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== index))} aria-label="Odebrat">×</CButton>
                    )}
                  </CCol>
                </CRow>
              ))}
              <CButton size="sm" color="primary" variant="outline" onClick={() => setItems([...items, { portalDeviceTypeId: '', customName: '', quantity: 1, label: '' }])}>
                Další zařízení
              </CButton>
              <div className="small text-body-secondary mt-3">
                Konfiguraci — krabici, antény, parametry — teď vyplňovat nemusíte. Doplní se v detailu zakázky.
              </div>
            </CCardBody>
          </CCard>

          {error && <CAlert color="danger">{error}</CAlert>}
          <CButton color="primary" className="w-100" data-tour="nc-submit" disabled={busy || !customerReady || !title.trim() || !itemsReady} onClick={() => void submit()}>
            Založit zakázku
          </CButton>
        </CCol>
      </CRow>
    </>
  );
}
